import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import type { ImporterState } from 'hello-csv/react';
import { error } from '../../../../ErrorHandling';
import { errorAlert, FlashAlert } from '../../../../Notifications/Types';
import { TPlainTextKey } from '../../../../Translations';
import { SelectableUserGroup } from '../../../Form/GroupInputWithSuggestions/Types';
import { UserGroupWithAssociations } from '../../Participants/AddParticipant/Stores/ParticipantOptionsStore/Types';
import {
  areLicensesAuditor,
  fileProcessed,
  helloCsvOpen,
  ImportOptionsResource,
  ImportResultResource,
  License,
  loadingOptions,
  optionsLoadError,
  optionsReady,
  ParticipantCsvRow,
  SelectableLicense,
  State,
  submitFailed,
  submitSucceeded,
  submitting,
  viewReport,
  ViewReportAction,
  waiting,
} from './Types';

class ImportParticipantsV2Store {
  @observable
  state: State = waiting();

  @observable
  savedHelloCsvState: ImporterState | null = null;

  constructor() {
    makeObservable(this);
  }

  @action
  saveHelloCsvState = (importerState: ImporterState) => {
    this.savedHelloCsvState = importerState;
  };

  @action
  load = () => {
    switch (this.state.kind) {
      case 'waiting':
        this.state = loadingOptions();
        break;
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  reset = () => {
    this.state = waiting();
    this.savedHelloCsvState = null;
  };

  // "Add More Participants" — clear the previous batch's CSV rows and result
  // but reuse the already-loaded options and keep the admin's group/license
  // selections (in importFormStore) so they can add another batch to the same
  // set without re-selecting. Mirrors AddParticipant's "Add Another" behavior.
  @action
  prepareForNextImport = () => {
    switch (this.state.kind) {
      case 'submit-succeeded':
      case 'view-report':
        this.savedHelloCsvState = null;
        this.state = optionsReady(this.state.optionsResource);
        break;
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  openHelloCsv = () => {
    switch (this.state.kind) {
      case 'options-ready':
      case 'file-processed':
      case 'submit-failed':
        this.state = helloCsvOpen(this.state);
        break;
      case 'waiting':
      case 'loading-options':
      case 'hellocsv-open':
      case 'submitting':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  closeHelloCsv = () => {
    switch (this.state.kind) {
      case 'hellocsv-open': {
        const state = this.state;
        state.participants
          .filter((ps) => ps.length > 0)
          .andThen((participants) => state.filename.map((filename) => ({ participants, filename })))
          .cata({
            Just: ({ participants, filename }) => {
              this.state = {
                kind: 'file-processed',
                optionsResource: state.optionsResource,
                selectedGroups: state.selectedGroups,
                selectedLicenses: state.selectedLicenses,
                participants,
                filename,
              };
            },
            Nothing: () => {
              this.state = {
                kind: 'options-ready',
                optionsResource: state.optionsResource,
                selectedGroups: state.selectedGroups,
                selectedLicenses: state.selectedLicenses,
              };
            },
          });
        break;
      }
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  fileProcessed = (participants: ParticipantCsvRow[], filename: string) => {
    switch (this.state.kind) {
      case 'hellocsv-open':
        this.state = fileProcessed(this.state, participants, filename);
        break;
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submit = () => {
    switch (this.state.kind) {
      case 'file-processed':
      case 'submit-failed':
        this.state = submitting(this.state);
        break;
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'submitting':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submitSucceeded = (resultResource: ImportResultResource) => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submitSucceeded(this.state, resultResource);
        break;
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  viewReport = (view: ViewReportAction) => {
    switch (this.state.kind) {
      case 'submit-succeeded':
        this.state = viewReport(this.state, view);
        break;
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setError = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submitFailed(this.state, msg);
        break;
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setOptionsReady = (resource: ImportOptionsResource) => {
    switch (this.state.kind) {
      case 'loading-options':
      case 'waiting': // Handle race condition from React StrictMode double-mounting
        this.state = optionsReady(resource);
        break;
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setOptionsLoadError = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'loading-options':
        this.state = optionsLoadError(msg);
        break;
      case 'waiting':
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  // Required by ErrorActionable interface for ErrorActionableReaction. Submit
  // failures keep the processed file and selections so the user can retry;
  // everything else falls through to the terminal error state.
  @action
  error = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submitFailed(this.state, msg);
        break;
      default:
        this.state = error(msg);
    }
  };

  @action
  removeParticipantAtIndex = (index: number) => {
    switch (this.state.kind) {
      case 'file-processed':
      case 'submit-failed': {
        const removedParticipant = this.state.participants[index];
        const newParticipants = this.state.participants.filter((_, i) => i !== index);

        // Also update savedHelloCsvState to remove the corresponding row
        if (this.savedHelloCsvState && removedParticipant) {
          const updatedSheetData = this.savedHelloCsvState.sheetData.map((sheet) => {
            if (sheet.sheetId === 'participants') {
              return {
                ...sheet,
                rows: sheet.rows.filter(
                  (row) =>
                    String(row.email || '')
                      .trim()
                      .toLowerCase() !== removedParticipant.email.toLowerCase(),
                ),
              };
            }
            return sheet;
          });
          this.savedHelloCsvState = {
            ...this.savedHelloCsvState,
            sheetData: updatedSheetData,
          };
        }

        if (newParticipants.length === 0) {
          // If no participants left, go back to options-ready state and clear saved state
          this.savedHelloCsvState = null;
          this.state = {
            kind: 'options-ready',
            optionsResource: this.state.optionsResource,
            selectedGroups: this.state.selectedGroups,
            selectedLicenses: this.state.selectedLicenses,
          };
        } else {
          this.state = { ...this.state, participants: newParticipants };
        }
        break;
      }
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'submitting':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  // Computed properties
  @action
  setSelectedGroups = (groups: SelectableUserGroup[]) => {
    switch (this.state.kind) {
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submit-failed':
        this.state = { ...this.state, selectedGroups: groups };
        break;
      case 'waiting':
      case 'loading-options':
      case 'submitting':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setSelectedLicenses = (licenses: SelectableLicense[]) => {
    switch (this.state.kind) {
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submit-failed':
        this.state = { ...this.state, selectedLicenses: licenses };
        break;
      case 'waiting':
      case 'loading-options':
      case 'submitting':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get availableUserGroups(): UserGroupWithAssociations[] {
    switch (this.state.kind) {
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
        return this.state.optionsResource.payload.userGroups;
      case 'waiting':
      case 'loading-options':
      case 'options-load-error':
      case 'error':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get availableLicenses(): License[] {
    switch (this.state.kind) {
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
        return this.state.optionsResource.payload.licenses;
      case 'waiting':
      case 'loading-options':
      case 'options-load-error':
      case 'error':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get canAddAsAuditor(): boolean {
    switch (this.state.kind) {
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
        return this.state.optionsResource.payload.canAddAsAuditor;
      case 'waiting':
      case 'loading-options':
      case 'options-load-error':
      case 'error':
        return false;
      default:
        assertNever(this.state);
    }
  }

  @computed
  get allowedEmailDomains(): string[] {
    switch (this.state.kind) {
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
      case 'view-report':
        return this.state.optionsResource.payload.allowedEmailDomains;
      case 'waiting':
      case 'loading-options':
      case 'options-load-error':
      case 'error':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get selectedGroups(): SelectableUserGroup[] {
    switch (this.state.kind) {
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
        return this.state.selectedGroups;
      case 'waiting':
      case 'loading-options':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get selectedLicenses(): SelectableLicense[] {
    switch (this.state.kind) {
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
      case 'submit-succeeded':
        return this.state.selectedLicenses;
      case 'waiting':
      case 'loading-options':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get areLicensesAuditor(): boolean {
    return areLicensesAuditor(this.selectedLicenses);
  }

  @computed
  get notification(): Maybe<FlashAlert> {
    switch (this.state.kind) {
      case 'error':
        return just(this.state).map(errorAlert);
      case 'submit-failed':
      case 'options-load-error':
        return just(errorAlert({ kind: 'error', message: this.state.message }));
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'file-processed':
      case 'submitting':
      case 'submit-succeeded':
      case 'view-report':
        return nothing();
      default:
        assertNever(this.state);
    }
  }

  @computed
  get isSubmitting(): boolean {
    return this.state.kind === 'submitting';
  }

  @computed
  get hasParticipantsToImport(): boolean {
    switch (this.state.kind) {
      case 'file-processed':
      case 'submit-failed':
        return this.state.participants.length > 0;
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'submitting':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        return false;
      default:
        assertNever(this.state);
    }
  }

  @computed
  get participantCount(): number {
    switch (this.state.kind) {
      case 'file-processed':
      case 'submitting':
      case 'submit-failed':
        return this.state.participants.length;
      case 'waiting':
      case 'loading-options':
      case 'options-ready':
      case 'hellocsv-open':
      case 'submit-succeeded':
      case 'view-report':
      case 'options-load-error':
      case 'error':
        return 0;
      default:
        assertNever(this.state);
    }
  }
}

const importParticipantsV2Store = new ImportParticipantsV2Store();

export default ImportParticipantsV2Store;
export { importParticipantsV2Store };
